import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  BAR_HEIGHT_PX,
  LEFT_COLUMN_WIDTH_PX,
  TIMELINE_INDENT_PX,
  TIMELINE_ROW_HEIGHT_PX,
} from './timelineGeometry';
import { barGeometry } from './timelineModel';
import type { TimelineSpan } from './timelineModel';
import type { RowDragFeedback, RowDragState } from './useRowDrag';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { zIndex } from '@/theme';

/**
 * Every mark drawn inside the timeline's scroller during a restructure drag.
 *
 * All of it is positioned in LIST-SPACE — `top = index * rowHeight`, `left`
 * measured from the track's origin — never from a row's bounding rect. A row
 * element is `260 + dayCount × dayWidth` px wide and lives inside the horizontal
 * scroller, so its `rect.left` goes negative the moment the track is scrolled
 * right. Rows are a fixed height with no dynamic measurement, so index maths is
 * exact (ADR 0019).
 *
 * Everything here is `pointerEvents: 'none'`: the drag hit-tests with
 * `elementFromPoint`, and an overlay that could be hit would shadow the rows.
 */

export type TimelineDropOverlayProps = {
  drag: RowDragState;
  feedback: RowDragFeedback | null;
  totalWidth: number;
  /** Current zoom, px per day. */
  dayWidth: number;
  /** Bar span of the prospective parent, for the growth preview. */
  parentSpan: TimelineSpan | null;
  /** Bar span of the node being dragged. */
  dragSpan: TimelineSpan | null;
};

const rowTop = (index: number) => index * TIMELINE_ROW_HEIGHT_PX;

export const TimelineDropOverlay = ({
  drag,
  feedback,
  totalWidth,
  dayWidth,
  parentSpan,
  dragSpan,
}: TimelineDropOverlayProps) => {
  const { t } = useTranslation('pmPlanning');
  const move = feedback?.outcome.kind === 'move' ? feedback.outcome : null;
  const gapIndex = move === null ? null : (feedback?.gapIndex ?? null);
  const lineDepth = feedback?.depth ?? 0;
  /*
    Only a reparent tints the destination and previews its bar. A reorder among
    existing siblings changes no parent, so highlighting one would imply a
    nesting change that is not happening.
  */
  const parentIndex = move !== null && move.reparenting ? (feedback?.parentIndex ?? null) : null;

  // The parent's bar with the dragged node's dates folded in — what ADR 0018's
  // union rule will produce the moment this drop commits.
  const grownSpan =
    parentSpan && dragSpan
      ? {
          startOffset: Math.min(parentSpan.startOffset, dragSpan.startOffset),
          endOffset: Math.max(parentSpan.endOffset, dragSpan.endOffset),
        }
      : (parentSpan ?? dragSpan);

  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: zIndex.dragOverlay }}>
      {/* The travelling block, bracketed in the frozen column. */}
      <Box
        data-automation-id="timeline-drag-block"
        sx={{
          position: 'absolute',
          top: `${String(rowTop(drag.fromIndex))}px`,
          height: `${String((drag.toIndex - drag.fromIndex + 1) * TIMELINE_ROW_HEIGHT_PX)}px`,
          left: 0,
          width: `${String(totalWidth)}px`,
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            width: `${String(LEFT_COLUMN_WIDTH_PX)}px`,
            height: '100%',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '8px',
              top: 0,
              bottom: 0,
              width: '3px',
              borderRadius: 'var(--border-radius-small)',
              bgcolor: 'var(--color-blue-40)',
            }}
          />
        </Box>
      </Box>

      {parentIndex !== null && (
        <>
          <Box
            data-automation-id="timeline-drop-parent"
            sx={{
              position: 'absolute',
              top: `${String(rowTop(parentIndex))}px`,
              height: `${String(TIMELINE_ROW_HEIGHT_PX)}px`,
              left: 0,
              width: `${String(totalWidth)}px`,
              bgcolor: 'var(--color-blue-20)',
              opacity: 0.35,
              borderTop: '2px solid var(--color-blue-80)',
              borderBottom: '2px solid var(--color-blue-80)',
            }}
          />
          {/* Nested outside the tinted box so the pill reads at full strength. */}
          <Box
            sx={{
              position: 'absolute',
              top: `${String(rowTop(parentIndex))}px`,
              height: `${String(TIMELINE_ROW_HEIGHT_PX)}px`,
              left: 0,
              width: `${String(totalWidth)}px`,
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                left: 0,
                width: `${String(LEFT_COLUMN_WIDTH_PX)}px`,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                pr: 1,
              }}
            >
              <Box
                data-automation-id="timeline-drop-parent-pill"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  borderRadius: 'var(--border-radius-pill)',
                  bgcolor: 'var(--color-blue-10)',
                }}
              >
                <MaterialSymbol
                  name="subdirectory_arrow_right"
                  size={14}
                  color="var(--color-blue-80)"
                />
                <Typography variant="label" sx={{ color: 'var(--color-blue-100)' }}>
                  {t('timeline.DropInside')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/*
            The Gantt-only signal: the parent's bar grows to swallow the dragged
            node's dates. Dashed outline over its existing solid bar, so the
            schedule consequence of the reparent is visible before committing.
          */}
          {grownSpan && (
            <Box
              data-automation-id="timeline-drop-parent-bar"
              sx={{
                position: 'absolute',
                top: `${String(
                  rowTop(parentIndex) + (TIMELINE_ROW_HEIGHT_PX - BAR_HEIGHT_PX) / 2,
                )}px`,
                height: `${String(BAR_HEIGHT_PX)}px`,
                left: `${String(LEFT_COLUMN_WIDTH_PX + barGeometry(grownSpan, dayWidth).left)}px`,
                width: `${String(barGeometry(grownSpan, dayWidth).width)}px`,
                border: '2px dashed var(--color-blue-80)',
                borderRadius: 'var(--border-radius-small)',
              }}
            />
          )}
        </>
      )}

      {gapIndex !== null && (
        <Box
          data-automation-id="timeline-drop-line"
          sx={{
            position: 'absolute',
            top: `${String(rowTop(gapIndex) - 1)}px`,
            height: '2px',
            left: 0,
            width: `${String(totalWidth)}px`,
            overflow: 'hidden',
          }}
        >
          {/* Confined to the name column: a line across the track would read
              as a date, not as a nesting level. */}
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              width: `${String(LEFT_COLUMN_WIDTH_PX)}px`,
              height: '2px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                height: '2px',
                borderRadius: 'var(--border-radius-small)',
                bgcolor: 'var(--color-blue-80)',
                // Animated, so an arrow-key outdent visibly slides the line out.
                transition: 'margin-left var(--duration-instant)',
                marginLeft: `${String(lineDepth * TIMELINE_INDENT_PX + 16)}px`,
                marginRight: '12px',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '-4px',
                  top: '-3px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  bgcolor: 'var(--color-blue-80)',
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
